import React, { useContext, useEffect, useMemo, useState } from 'react';
import * as R from 'ramda';

import InventoryAssetTree from '@atom/components/common/inventoryAssetTree/InventoryAssetTree';
import { TYPE_LABELS } from '@atom/components/common/requests/assetRequestConstants';
import { getCustomValues } from '@atom/components/common/requests/customTenantUtilities';
import { Icon, IconButton, Modal, Select } from '@atom/mui';
import { ArrowBackIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { AssetConnectionItem } from '@atom/types/asset';
import {
  AssetRequestCreateInput,
  AssetRequestType,
} from '@atom/types/assetRequest';
import { UserDetail } from '@atom/types/user';
import {
  isAssetRefreshOnRequestCreationEnabled,
  isAssetRequestTypeFilterDisabled,
} from '@atom/utilities/tenantFeatureUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import InventoryDetailContext from '../../InventoryDetailContext';
import useAssetUsage from '../../useAssetUsage';
import RequestsContext from '../RequestsContext';

import ADOTAdjustmentCreate from './adotCreate/ADOTAdjustmentCreate';
import ADOTReplenishmentCreate from './adotCreate/ADOTReplenishmentCreate';
import ADOTTransferCreate from './adotCreate/ADOTTransferCreate';
import ARDOT118TransferCreate from './ardotCreate/ARDOT118TransferCreate';
import SDDOTAdjustmentCreate from './sddotCreate/SDDOTAdjustmentCreate';
import SDDOTInventoryCreate from './sddotCreate/SDDOTInventoryCreate';
import SDDOTReplenishmentCreate from './sddotCreate/SDDOTReplenishmentCreate';
import SDDOTStockpileCreate from './sddotCreate/SDDOTStockpileCreate';
import { getCreateDisabled } from './createRequestUtilities';

const { MenuItem } = Select;

const styles = {
  modal: {
    padding: '1.5rem',
  },
  select: {
    maxWidth: '28.25rem',
  },
  back: {
    marginRight: '1rem',
  },
  category: {
    minHeight: '32rem',
  },
};

export enum View {
  MAIN = 'MAIN',
  CATEGORY = 'CATEGORY',
}

type CreateRequestModalProps = {
  open: boolean;
  onClose: () => void;
  createInputs: AssetRequestCreateInput;
  updateCreateInput: (
    value: any,
    property: keyof AssetRequestCreateInput,
  ) => void;
  onConfirm: () => void;
  updateType: (type: any) => void;
};

const CreateRequestModal = ({
  open,
  onClose,
  createInputs,
  updateCreateInput,
  onConfirm,
  updateType,
}: CreateRequestModalProps) => {
  const { refetch } = useContext(InventoryDetailContext);
  const { schema, asset } = useContext(RequestsContext);

  const [view, setView] = useState<View>(View.MAIN);
  const [budgetLoading, setBudgetLoading] = useState<boolean>(false);
  const [selectedFromAsset, setSelectedFromAsset] =
    useState<AssetConnectionItem>();
  const [transferToggle, setTransferToggle] = useState<boolean>(false);
  const [selectedOperatorUser, setSelectedOperatorUser] =
    useState<UserDetail>();

  const budget = asset?.budget;

  // TODO: [AM-16707]: Temp code until BE solution is built
  const {
    summary: usageSummary,
    summaryLoading: totalRemainingLoading,
    fetchSummary: fetchUsageSummary,
  } = useAssetUsage({ assetId: asset?.id });

  const {
    summary: fromUsageSummary,
    summaryLoading: fromTotalRemainingLoading,
    fetchSummary: fetchFromUsageSummary,
  } = useAssetUsage({ assetId: selectedFromAsset?.id });

  const totalRemaining = R.pathOr(0, ['totalRemaining'], usageSummary);
  const fromTotalRemaining = R.pathOr(0, ['totalRemaining'], fromUsageSummary);

  useEffect(() => {
    if (asset?.id) {
      fetchUsageSummary();
    }
  }, [asset?.id]);

  useEffect(() => {
    if (selectedFromAsset?.id) {
      fetchFromUsageSummary();
    }
  }, [selectedFromAsset?.id]);

  const refreshAsset = async () => {
    setBudgetLoading(true);
    await refetch().catch(() => undefined);
    setBudgetLoading(false);
  };

  useEffect(() => {
    if (open && isAssetRefreshOnRequestCreationEnabled) {
      refreshAsset();
      setSelectedFromAsset(null);
      setView(View.MAIN);
    }
  }, [open]);

  useEffect(() => {
    setSelectedFromAsset(null);
  }, [createInputs.type]);

  const createProps = {
    open,
    createInputs,
    updateCreateInput,
    budget,
    asset,
    // TODO: [AM-16707]: Temp code until BE solution is built
    totalRemaining,
    fromTotalRemaining,
    totalRemainingLoading,
    fromTotalRemainingLoading,
  };

  const modalContent = {
    [AssetRequestType.SDDOT_INVENTORY_TRANSFER]: (
      <SDDOTInventoryCreate
        {...createProps}
        selectedFromAsset={selectedFromAsset}
        setSelectedFromAsset={setSelectedFromAsset}
      />
    ),
    [AssetRequestType.SDDOT_STOCKPILE_TRANSFER]: (
      <SDDOTStockpileCreate
        {...createProps}
        selectedFromAsset={selectedFromAsset}
        setSelectedFromAsset={setSelectedFromAsset}
      />
    ),
    [AssetRequestType.SDDOT_REPLENISHMENT]: (
      <SDDOTReplenishmentCreate {...createProps} />
    ),
    [AssetRequestType.SDDOT_ADJUSTMENT]: (
      <SDDOTAdjustmentCreate {...createProps} />
    ),
    [AssetRequestType.ARDOT_118_TRANSFER]: (
      <ARDOT118TransferCreate
        {...createProps}
        setView={setView}
        transferToggle={transferToggle}
        setTransferToggle={setTransferToggle}
        selectedOperatorUser={selectedOperatorUser}
        setSelectedOperatorUser={setSelectedOperatorUser}
      />
    ),
    [AssetRequestType.ADOT_INVENTORY_TRANSFER]: (
      <ADOTTransferCreate
        {...createProps}
        selectedFromAsset={selectedFromAsset}
        setSelectedFromAsset={setSelectedFromAsset}
      />
    ),
    [AssetRequestType.ADOT_ADJUSTMENT]: (
      <ADOTAdjustmentCreate {...createProps} />
    ),
    [AssetRequestType.ADOT_REPLENISHMENT]: (
      <ADOTReplenishmentCreate {...createProps} />
    ),
  };

  const disabled = useMemo(
    () =>
      getCreateDisabled(createInputs, {
        // TODO: [AM-16707]: Temp code until BE solution is built
        totals: {
          totalRemaining,
          fromTotalRemaining,
          totalRemainingLoading,
          fromTotalRemainingLoading,
        },
        transferToggle,
      }),
    [
      createInputs,
      // TODO: [AM-16707]: Temp code until BE solution is built
      totalRemaining,
      fromTotalRemaining,
      totalRemainingLoading,
      fromTotalRemainingLoading,
      transferToggle,
    ],
  );

  const { typeOptions } = getCustomValues(schema, asset);

  const modalWidth =
    createInputs?.type === AssetRequestType.SDDOT_REPLENISHMENT ||
    createInputs?.type === AssetRequestType.ADOT_REPLENISHMENT
      ? 'xl'
      : 'sm';

  const getTitle = (): React.ReactNode => {
    if (view === View.CATEGORY) {
      return (
        <>
          <IconButton
            style={styles.back}
            onClick={() => setView(View.MAIN)}
            data-cy="createAssetRequestCategoryBackButton"
          >
            <Icon icon={ArrowBackIcon} />
          </IconButton>
          To District or Division
        </>
      );
    }

    return 'Create Inventory Request';
  };

  const handleCategorySelect = (category: any, path: any) => {
    if (isNilOrEmpty(category?.categories)) {
      updateCreateInput(null, 'reviewer');
      updateCreateInput(null, 'receiver');
      updateCreateInput(category?.id, 'toCategoryId');
      updateCreateInput(
        path.map(item => item.name).join(' / '),
        'toCategoryPath',
      );

      setView(View.MAIN);
    }
  };

  const getContent = (): React.ReactNode => {
    const content = {
      [View.MAIN]: (
        <div>
          {!isAssetRequestTypeFilterDisabled && (
            <Select
              key="type"
              label="Request Type"
              style={styles.select}
              InputLabelProps={{ style: { color: colors.neutral.dim } }}
              fullWidth
              onChange={event => updateType(event.target.value)}
              value={createInputs.type}
              data-cy="assetRequestTypeSelect"
            >
              {typeOptions.map((option: string, index: number) => (
                <MenuItem key={index} value={option}>
                  {TYPE_LABELS[option]}
                </MenuItem>
              ))}
            </Select>
          )}
          {modalContent[createInputs.type]}
        </div>
      ),
      [View.CATEGORY]: (
        <div style={styles.category}>
          <InventoryAssetTree
            includeAssets={false}
            search="categories"
            onCategoryClick={(category, path) =>
              handleCategorySelect(category, path)
            }
            disabledCategoryIds={
              new Set(['inventory', createInputs.categoryId])
            }
            schemaId={schema?.id}
            includeMaterials
            onlyPublishedCategories
            onlyCategoriesWithSchema
          />
        </div>
      ),
    };

    return content[view];
  };

  return (
    <Modal
      title={getTitle()}
      open={open}
      onCancel={onClose}
      onConfirm={onConfirm}
      confirmButtonText="Create"
      data-cy="createAssetRequest"
      contentStyle={styles.modal}
      disabled={disabled}
      width={modalWidth}
      loading={budgetLoading}
      disableCloseOnOutsideClick
      disableFooter={view === View.CATEGORY}
      CancelButtonProps={{ 'data-cy': 'createAssetRequestCancel' }}
      ConfirmButtonProps={{ 'data-cy': 'createAssetRequestConfirm' }}
    >
      {getContent()}
    </Modal>
  );
};

export default CreateRequestModal;
